<template>
  <div v-if="job" class="ocr-panel ocr-process-status">
    <div class="ocr-process-status-head">
      <div>
        <div class="ocr-panel-title">Processing status</div>
        <p class="ocr-process-status-file">{{ isRunning ? fileLine : doneLine }}</p>
      </div>
      <span v-if="isRunning" class="ocr-file-count-chip">{{ progressLabel }}</span>
    </div>

    <template v-if="isRunning">
    <v-progress-linear
      :model-value="mainPercent"
      height="10"
      rounded
      color="primary"
      :indeterminate="indeterminate && isRunning"
    />
    <p class="ocr-process-status-caption">{{ mainText }}</p>

    <v-progress-linear
      v-if="!isExcel"
      :model-value="stepPercent"
      height="8"
      rounded
      color="info"
      class="ocr-process-status-step-bar"
    />
    <p v-if="!isExcel && stepText" class="ocr-process-status-caption">{{ stepText }}</p>

    <ul v-if="steps.length" class="ocr-process-steps">
      <li
        v-for="step in steps"
        :key="step.id"
        class="ocr-process-step"
        :class="`ocr-process-step--${step.status}`"
      >
        <span class="ocr-process-step-mark" aria-hidden="true">{{ step.icon }}</span>
        <span class="ocr-process-step-name">{{ step.label }}</span>
        <span class="ocr-process-step-msg">{{ step.message }}</span>
      </li>
    </ul>

    <ul v-if="warnings.length" class="ocr-process-warnings">
      <li v-for="(note, i) in warnings" :key="i">{{ note }}</li>
    </ul>

    <div class="ocr-process-stats">
      <div>
        <span class="ocr-process-stat-label">Elapsed</span>
        <span class="ocr-process-stat-value">{{ formatDuration(elapsed) }}</span>
      </div>
      <div>
        <span class="ocr-process-stat-label">Succeeded</span>
        <span class="ocr-process-stat-value">{{ job.success_count || 0 }}</span>
      </div>
      <div>
        <span class="ocr-process-stat-label">Failed</span>
        <span class="ocr-process-stat-value">{{ job.failed_count || 0 }}</span>
      </div>
    </div>
    </template>

    <div v-else class="ocr-process-summary">
      <ul v-if="warnings.length" class="ocr-process-warnings">
        <li v-for="(note, i) in warnings" :key="'done-' + i">{{ note }}</li>
      </ul>
      <div>
        <span class="ocr-process-stat-label">Elapsed</span>
        <span class="ocr-process-stat-value">{{ formatDuration(elapsed) }}</span>
      </div>
      <div>
        <span class="ocr-process-stat-label">Succeeded</span>
        <span class="ocr-process-stat-value">{{ job.success_count || 0 }}</span>
        <ul v-if="successFiles.length" class="ocr-process-file-list">
          <li v-for="name in successFiles" :key="'ok-' + name">{{ name }}</li>
        </ul>
      </div>
      <div>
        <span class="ocr-process-stat-label">Failed</span>
        <span class="ocr-process-stat-value">{{ job.failed_count || 0 }}</span>
        <ul v-if="failedDetails.length" class="ocr-process-file-list">
          <li v-for="item in failedDetails" :key="'bad-' + item.name">
            {{ item.name }}
            <span v-if="item.message"> — {{ item.message }}</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
const STEP_ORDER = [
  "image_preparation",
  "ocr",
  "table_detection",
  "data_extraction",
  "report_processing",
];

const STEP_ICONS = {
  waiting: "⏳",
  in_progress: "🔄",
  completed: "✅",
  failed: "❌",
};

export default {
  name: "OcrProcessStatus",
  props: {
    job: { type: Object, default: null },
  },
  data() {
    return {
      tick: Date.now(),
      clock: null,
      elapsedBase: 0,
      elapsedBaseAt: Date.now(),
      trackedJobId: null,
    };
  },
  computed: {
    isExcel() {
      return this.job?.kind === "excel";
    },
    fileLine() {
      if (this.job?.current_file) return `Current file: ${this.job.current_file}`;
      return this.job?.message || "Waiting…";
    },
    doneLine() {
      return this.job?.message || "Processing finished.";
    },
    successFiles() {
      return Array.isArray(this.job?.success_files) ? this.job.success_files : [];
    },
    failedFiles() {
      return Array.isArray(this.job?.failed_files) ? this.job.failed_files : [];
    },
    failedDetails() {
      const results = Array.isArray(this.job?.results) ? this.job.results : [];
      const failed = results.filter((item) => item.status === "failed");
      if (failed.length) {
        return failed.map((item) => ({
          name: item.file,
          message: item.error || item.message || "",
        }));
      }
      return this.failedFiles.map((name) => ({ name, message: "" }));
    },
    isRunning() {
      return ["queued", "running"].includes(this.job?.status);
    },
    indeterminate() {
      return this.isExcel && this.isRunning;
    },
    progress() {
      return this.job?.progress || {};
    },
    mainPercent() {
      return Math.round(Math.min(1, Math.max(0, Number(this.progress.main_progress) || 0)) * 100);
    },
    stepPercent() {
      return Math.round(Math.min(1, Math.max(0, Number(this.progress.step_progress) || 0)) * 100);
    },
    mainText() {
      return this.progress.main_text || this.job?.message || "";
    },
    stepText() {
      return this.progress.step_text || (this.job?.current_step
        ? this.job.current_step.replace(/_/g, " ")
        : "");
    },
    progressLabel() {
      const total = this.job?.total_files || this.job?.files?.length || 0;
      const done = (this.job?.processed_count
        ?? ((this.job?.success_count || 0) + (this.job?.failed_count || 0)));
      if (!total) return this.job?.status || "";
      return `${done} / ${total}`;
    },
    elapsed() {
      if (!this.isRunning) {
        const server = Number(this.job?.elapsed_seconds ?? this.progress.total_time);
        return Number.isFinite(server) ? server : null;
      }
      const extra = Math.max(0, (this.tick - this.elapsedBaseAt) / 1000);
      return this.elapsedBase + extra;
    },
    warnings() {
      const notes = this.job?.warnings || this.job?.progress?.warnings || [];
      return Array.isArray(notes) ? notes.filter(Boolean) : [];
    },
    steps() {
      if (this.isExcel) return [];
      const raw = this.job?.step_status || this.progress.step_status || {};
      return STEP_ORDER.map((id) => {
        const value = raw[id];
        const status = Array.isArray(value) ? value[0] : "waiting";
        const message = Array.isArray(value) ? value[1] : "Waiting...";
        return {
          id,
          label: id.replace(/_/g, " "),
          status,
          message,
          icon: STEP_ICONS[status] || "•",
        };
      });
    },
  },
  watch: {
    job: {
      immediate: true,
      handler(job) {
        const id = job?.job_id || null;
        const server = Number(job?.elapsed_seconds ?? job?.progress?.total_time);
        const serverElapsed = Number.isFinite(server) ? server : 0;
        if (id !== this.trackedJobId) {
          this.trackedJobId = id;
          this.elapsedBase = serverElapsed;
          this.elapsedBaseAt = Date.now();
          return;
        }
        if (serverElapsed > this.elapsedBase + 0.4) {
          this.elapsedBase = serverElapsed;
          this.elapsedBaseAt = Date.now();
        }
      },
    },
  },
  mounted() {
    this.clock = setInterval(() => {
      this.tick = Date.now();
    }, 1000);
  },
  beforeUnmount() {
    clearInterval(this.clock);
  },
  methods: {
    formatDuration(sec) {
      if (sec == null || Number.isNaN(Number(sec))) return "—";
      const s = Math.max(0, Math.round(Number(sec)));
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const r = s % 60;
      if (h) return `${h}h ${m}m`;
      if (m) return `${m}m ${r}s`;
      return `${r}s`;
    },
  },
};
</script>
